import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { LogoWord } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("customer");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let user;
      if (mode === "login") user = await login(form.email, form.password);
      else user = await register({ ...form, role });
      toast.success(`Welcome${user.name ? ", " + user.name.split(" ")[0] : ""}!`);
      navigate(user.role === "seller" ? "/seller" : user.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl border border-[#E5DEC9] shadow-sm p-6 sm:p-8 animate-fade-up">
        <div className="flex justify-center mb-5"><LogoWord /></div>
        <h1 className="font-display text-2xl font-bold text-[#1A3C2A] text-center">
          {mode === "login" ? "Welcome back" : "Join Nabor"}
        </h1>
        <p className="text-sm text-[#5A626A] text-center mt-1 mb-6">
          {mode === "login" ? "Sign in to shop and sell locally." : "Create your account to get started."}
        </p>

        {mode === "register" && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {["customer", "seller"].map((r) => (
              <button key={r} type="button" data-testid={`role-${r}`} onClick={() => setRole(r)}
                className={`py-2.5 rounded-xl text-sm font-semibold border capitalize transition-colors ${role === r ? "bg-[#1A3C2A] text-white border-[#1A3C2A]" : "bg-white text-[#22252A] border-[#E5DEC9]"}`}>
                {r === "customer" ? "I'm a shopper" : "I'm a seller"}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div>
              <Label className="text-xs">Full name</Label>
              <Input data-testid="auth-name" value={form.name} onChange={set("name")} required className="rounded-xl mt-1" placeholder="Jane Smith" />
            </div>
          )}
          <div>
            <Label className="text-xs">Email</Label>
            <Input data-testid="auth-email" type="email" value={form.email} onChange={set("email")} required className="rounded-xl mt-1" placeholder="you@example.com" />
          </div>
          <div>
            <Label className="text-xs">Password</Label>
            <Input data-testid="auth-password" type="password" value={form.password} onChange={set("password")} required className="rounded-xl mt-1" placeholder="••••••••" />
          </div>
          {error && <p data-testid="auth-error" className="text-sm text-red-600">{error}</p>}
          <Button type="submit" data-testid="auth-submit" disabled={loading}
            className="w-full rounded-xl bg-[#1A3C2A] hover:bg-[#132E20] h-11 font-semibold">
            {loading ? <Loader2 className="animate-spin" size={18} /> : mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-[#E5DEC9]" /><span className="text-xs text-[#9A9282]">or</span><div className="flex-1 h-px bg-[#E5DEC9]" />
        </div>
        <Button type="button" variant="outline" data-testid="auth-google" onClick={google}
          className="w-full rounded-xl border-[#E5DEC9] h-11 font-semibold gap-2">
          <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
        </Button>

        <p className="text-sm text-center text-[#5A626A] mt-5">
          {mode === "login" ? "New to Nabor? " : "Already have an account? "}
          <button data-testid="auth-toggle" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="font-semibold text-[#E07A5F] hover:underline">
            {mode === "login" ? "Create an account" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
}
